import React, { useState, useEffect } from 'react';
import { AdminDataTable, AdminActionModal, AdminMetricCard, AdminChart } from '../components/admin';
import api from '../services/api';
import { CreditCardIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const FEATURE_LIST = [
  { key: 'member_management', label: 'Member Management' },
  { key: 'attendance_tracking', label: 'Attendance Tracking' },
  { key: 'payment_processing', label: 'Payment Processing' },
  { key: 'basic_reports', label: 'Basic Reports' },
  { key: 'trainer_management', label: 'Trainer Management' },
  { key: 'notifications', label: 'Notifications & Messaging' },
  { key: 'analytics_dashboard', label: 'Analytics Dashboard' },
  { key: 'whatsapp_integration', label: 'WhatsApp Integration' },
  { key: 'biometric_integration', label: 'Biometric Integration' },
  { key: 'inventory_management', label: 'Inventory Management' },
  { key: 'workout_diet_plans', label: 'Workout & Diet Plans' },
  { key: 'bulk_upload', label: 'Bulk Member Upload' },
  { key: 'multi_branch_support', label: 'Multi-Branch Support' },
  { key: 'priority_support', label: 'Priority Support' },
  { key: 'custom_branding', label: 'Custom Branding' },
  { key: 'api_access', label: 'API Access' }
];

function EditSubscriptionModal({ isOpen, onClose, subscription, onSave, loading }) {
  const [planName, setPlanName] = useState('');
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [maxMembers, setMaxMembers] = useState('');
  const [maxTrainers, setMaxTrainers] = useState('');
  const [autoRenew, setAutoRenew] = useState(true);
  const [features, setFeatures] = useState({});

  useEffect(() => {
    if (subscription) {
      setPlanName(subscription.plan_name || 'Basic');
      setMonthlyPrice(subscription.monthly_price ?? 499);
      setMaxMembers(subscription.max_members ?? 50);
      setMaxTrainers(subscription.max_trainers ?? 2);
      setAutoRenew(subscription.auto_renew ?? true);

      const featObj = typeof subscription.features === 'object' && subscription.features
        ? { ...subscription.features }
        : {};

      FEATURE_LIST.forEach(f => {
        if (featObj[f.key] === undefined) {
          featObj[f.key] = true;
        }
      });
      setFeatures(featObj);
    }
  }, [subscription, isOpen]);

  if (!isOpen || !subscription) return null;

  const toggleFeature = (key) => {
    setFeatures(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      plan_name: planName,
      monthly_price: parseFloat(monthlyPrice) || 0,
      max_members: parseInt(maxMembers, 10),
      max_trainers: parseInt(maxTrainers, 10),
      auto_renew: autoRenew,
      features: features
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white border border-gray-200 rounded-2xl p-6 max-w-2xl w-full shadow-xl my-8">
        <h3 className="text-xl font-bold text-gray-900 mb-1">
          Edit Subscription & Feature Permissions
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Gym: <strong className="text-gray-900">{subscription.gym_name}</strong>
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                Plan Name
              </label>
              <select
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              >
                <option value="Basic">Basic (₹499)</option>
                <option value="Pro">Pro (₹999)</option>
                <option value="Enterprise">Enterprise (₹2,499)</option>
                <option value="Custom">Custom Plan</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                Monthly Price (₹)
              </label>
              <input
                type="number"
                value={monthlyPrice}
                onChange={(e) => setMonthlyPrice(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                Max Members Limit (-1 = Unlimited)
              </label>
              <input
                type="number"
                value={maxMembers}
                onChange={(e) => setMaxMembers(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-xl text-sm font-mono focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                placeholder="50, 200, 500, or -1"
                required
              />
              <p className="text-[11px] text-gray-500 mt-1">Enter capacity or -1 for Unlimited members</p>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                Max Trainers Limit
              </label>
              <input
                type="number"
                value={maxTrainers}
                onChange={(e) => setMaxTrainers(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-xl text-sm font-mono focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                required
              />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="autoRenew"
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
              className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
            />
            <label htmlFor="autoRenew" className="text-sm font-medium text-gray-700">
              Auto-renew subscription automatically
            </label>
          </div>

          {/* Feature Checklist */}
          <div className="border-t border-gray-200 pt-4 space-y-3">
            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center justify-between">
              <span>Feature Permissions & Gating</span>
              <span className="text-xs font-normal text-gray-500">Tick box to enable, untick to disable</span>
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-3 bg-gray-50 border border-gray-200 rounded-xl">
              {FEATURE_LIST.map((feat) => {
                const isChecked = !!features[feat.key];
                return (
                  <label
                    key={feat.key}
                    className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                      isChecked ? 'bg-orange-50 text-orange-900 border border-orange-200' : 'bg-white text-gray-600 border border-gray-100 hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleFeature(feat.key)}
                      className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                    />
                    <span>{feat.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700 transition-colors shadow-sm disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save & Enforce Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPlatformPlanModal({ isOpen, onClose, plan, isCreate, onSave, loading }) {
  const [planName, setPlanName] = useState('');
  const [price, setPrice] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [description, setDescription] = useState('');
  const [recommended, setRecommended] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [features, setFeatures] = useState({});

  useEffect(() => {
    if (plan) {
      setPlanName(plan.plan_name || '');
      setPrice(plan.price ?? '');
      setBillingCycle(plan.billing_cycle || 'monthly');
      setDescription(plan.description || '');
      setRecommended(!!plan.recommended);
      setIsActive(plan.is_active !== undefined ? !!plan.is_active : true);

      const featObj = typeof plan.features === 'object' && plan.features ? { ...plan.features } : {};
      FEATURE_LIST.forEach(f => {
        if (featObj[f.key] === undefined) {
          featObj[f.key] = true;
        }
      });
      setFeatures(featObj);
    } else {
      setPlanName('');
      setPrice('');
      setBillingCycle('monthly');
      setDescription('');
      setRecommended(false);
      setIsActive(true);
      const defaultFeats = {};
      FEATURE_LIST.forEach(f => { defaultFeats[f.key] = true; });
      setFeatures(defaultFeats);
    }
  }, [plan, isOpen]);

  if (!isOpen) return null;

  const toggleFeature = (key) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      plan_name: planName,
      price: parseFloat(price) || 0,
      billing_cycle: billingCycle,
      currency: 'INR',
      description: description,
      recommended: recommended,
      is_active: isActive,
      features: features
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white border border-gray-200 rounded-2xl p-6 max-w-2xl w-full shadow-xl my-8">
        <h3 className="text-xl font-bold text-gray-900 mb-1">
          {isCreate ? "Create Platform Plan" : "Edit Platform Plan"}
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          {isCreate ? "Define a new plan with custom features & member limits" : "Update features, price and limits for this plan"}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                Plan Name
              </label>
              <input
                type="text"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder="e.g. Basic, Pro, Enterprise"
                className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                Monthly Price (₹)
              </label>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="e.g. 499"
                className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                Billing Cycle
              </label>
              <select
                value={billingCycle}
                onChange={(e) => setBillingCycle(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              >
                <option value="1 Month">1 Month</option>
                <option value="3 Months">3 Months</option>
                <option value="6 Months">6 Months</option>
                <option value="1 Year">1 Year</option>
              </select>
            </div>

            <div className="flex items-center gap-4 pt-6">
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={recommended}
                  onChange={(e) => setRecommended(e.target.checked)}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                Recommended Tag
              </label>
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                Active Plan
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="2"
              className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              placeholder="Plan summary..."
            />
          </div>

          {/* Feature Matrix Checklist */}
          <div className="border-t border-gray-200 pt-4 space-y-3">
            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center justify-between">
              <span>Included Features (Feature Matrix)</span>
              <span className="text-xs font-normal text-gray-500">Tick box to enable, untick to disable</span>
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-3 bg-gray-50 border border-gray-200 rounded-xl">
              {FEATURE_LIST.map((feat) => {
                const isChecked = !!features[feat.key];
                return (
                  <label
                    key={feat.key}
                    className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                      isChecked ? 'bg-orange-50 text-orange-900 border border-orange-200' : 'bg-white text-gray-600 border border-gray-100 hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleFeature(feat.key)}
                      className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                    />
                    <span>{feat.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700 transition-colors shadow-sm disabled:opacity-50"
            >
              {loading ? 'Saving...' : (isCreate ? 'Create Plan' : 'Save Plan Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Subscription Management interface for Super Admin
 * Provides subscription oversight, billing management, and analytics
 */
const SubscriptionManagement = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    plan_name: '',
    expiring_soon: false,
    page: 1,
    per_page: 20
  });

  // Platform plans state
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  // Modal states
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    subscription: null,
    plan: null,
    loading: false
  });

  // Subscription metrics
  const [metrics, setMetrics] = useState({
    total: 0,
    active: 0,
    expired: 0,
    expiring_soon: 0
  });

  useEffect(() => {
    fetchSubscriptions();
    fetchPlans();
  }, [filters]);

  // Fetch platform plans
  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await api.get('/api/admin/subscription-plans');
      setPlans(response.data.plans);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    } finally {
      setPlansLoading(false);
    }
  };

  // Handle plan actions
  const openPlanModal = (type, plan = null) => {
    setModalState({
      isOpen: true,
      type,
      plan,
      subscription: null,
      loading: false
    });
  };

  const handlePlanAction = async (action, plan, formData = {}) => {
    try {
      setModalState(prev => ({ ...prev, loading: true }));

      let response;
      switch (action) {
        case 'create':
          response = await api.post('/api/admin/subscription-plans', formData);
          break;
        case 'update':
          response = await api.put(`/api/admin/subscription-plans/${plan.id}`, formData);
          break;
        case 'delete':
          response = await api.delete(`/api/admin/subscription-plans/${plan.id}`);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      await fetchPlans();
      closeModal();

      console.log(`${action} successful:`, response.data.message);

    } catch (err) {
      console.error(`Failed to ${action} plan:`, err);
      setError(err.response?.data?.error || `Failed to ${action} plan`);
      setModalState(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.plan_name) params.append('plan_name', filters.plan_name);
      if (filters.expiring_soon) params.append('expiring_soon', 'true');
      params.append('page', filters.page);
      params.append('per_page', filters.per_page);

      const response = await api.get(`/api/admin/subscriptions?${params.toString()}`);
      
      setSubscriptions(response.data.subscriptions);
      setPagination(response.data.pagination);
      
      // Calculate metrics
      const counts = response.data.subscriptions.reduce(
        (acc, sub) => {
          acc.total++;
          if (sub.status === 'Active') acc.active++;
          if (sub.status === 'Expired') acc.expired++;
          if (sub.days_until_expiry <= 7 && sub.status === 'Active') acc.expiring_soon++;
          return acc;
        },
        { total: 0, active: 0, expired: 0, expiring_soon: 0 }
      );
      setMetrics(counts);
      
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      setError(err.response?.data?.error || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value
    }));
  };

  const openModal = (type, subscription) => {
    setModalState({
      isOpen: true,
      type,
      subscription,
      loading: false
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: null,
      subscription: null,
      loading: false
    });
  };

  const handleSubscriptionAction = async (action, subscription, formData = {}) => {
    try {
      setModalState(prev => ({ ...prev, loading: true }));

      let response;
      
      switch (action) {
        case 'update':
          response = await api.put(`/api/admin/subscriptions/${subscription.id}`, formData);
          break;
        case 'renew':
          response = await api.put(`/api/admin/subscriptions/${subscription.id}`, {
            billing_cycle_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          });
          break;
        case 'suspend':
          response = await api.put(`/api/admin/subscriptions/${subscription.id}`, {
            status: 'Suspended'
          });
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      await fetchSubscriptions();
      closeModal();
      
      console.log(`${action} successful:`, response.data.message);
      
    } catch (err) {
      console.error(`Failed to ${action} subscription:`, err);
      setError(err.response?.data?.error || `Failed to ${action} subscription`);
      setModalState(prev => ({ ...prev, loading: false }));
    }
  };

  // Table columns
  const columns = [
    { key: 'gym_name', label: 'Gym Name' },
    { key: 'plan_name', label: 'Plan' },
    { 
      key: 'monthly_price', 
      label: 'Monthly Price',
      render: (value) => `₹${value !== undefined && value !== null ? value.toLocaleString() : 0}`
    },
    { 
      key: 'max_members', 
      label: 'Member Limit',
      render: (value) => (value === -1 || value >= 99999 ? 'Unlimited' : value)
    },
    { 
      key: 'current_members', 
      label: 'Current Usage',
      render: (value, row) => `${value || 0}/${row.max_members === -1 || row.max_members >= 99999 ? '∞' : row.max_members}`
    },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value === 'Active' ? 'bg-green-100 text-green-800' :
          value === 'Expired' ? 'bg-red-100 text-red-800' :
          value === 'Suspended' ? 'bg-yellow-100 text-yellow-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {value}
        </span>
      )
    },
    {
      key: 'next_billing_date',
      label: 'Next Billing',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'days_until_expiry',
      label: 'Days Left',
      render: (value) => (
        <span className={`font-medium ${
          value <= 7 ? 'text-red-600' : 
          value <= 30 ? 'text-yellow-600' : 
          'text-green-600'
        }`}>
          {value} days
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, subscription) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openModal('edit', subscription);
            }}
            className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
          >
            Edit
          </button>
          
          {subscription.status === 'Active' && subscription.days_until_expiry <= 7 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openModal('renew', subscription);
              }}
              className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
            >
              Renew
            </button>
          )}
          
          {subscription.status === 'Active' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openModal('suspend', subscription);
              }}
              className="px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600"
            >
              Suspend
            </button>
          )}
        </div>
      )
    }
  ];

  // Filter options
  const statusFilters = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'Active', label: 'Active' },
        { value: 'Expired', label: 'Expired' },
        { value: 'Suspended', label: 'Suspended' }
      ]
    }
  ];

  const renderModal = () => {
    const { type, subscription, plan } = modalState;
    
    if (!type) return null;

    if (type === 'createPlan' || type === 'editPlan' || type === 'deletePlan') {
      if (type === 'deletePlan') {
        return (
          <AdminActionModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            loading={modalState.loading}
            title="Delete Plan"
            message={`Are you sure you want to delete the plan "${plan.plan_name}"?`}
            confirmText="Delete"
            type="warning"
            onConfirm={() => handlePlanAction('delete', plan)}
          />
        );
      }

      return (
        <EditPlatformPlanModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          plan={plan}
          isCreate={type === 'createPlan'}
          loading={modalState.loading}
          onSave={(processedData) => handlePlanAction(type === 'createPlan' ? 'create' : 'update', plan, processedData)}
        />
      );
    }

    if (type === 'edit' && subscription) {
      return (
        <EditSubscriptionModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          subscription={subscription}
          loading={modalState.loading}
          onSave={(formData) => handleSubscriptionAction('update', subscription, formData)}
        />
      );
    }

    if (!subscription) return null;

    const modalProps = {
      isOpen: modalState.isOpen,
      onClose: closeModal,
      loading: modalState.loading
    };

    switch (type) {
      case 'renew':
        return (
          <AdminActionModal
            {...modalProps}
            title="Renew Subscription"
            message={`Renew subscription for ${subscription.gym_name} for another 30 days?`}
            confirmText="Renew Now"
            type="info"
            onConfirm={() => handleSubscriptionAction('renew', subscription)}
          >
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
              <p className="text-sm text-blue-800">
                This will extend the billing cycle by 30 days from the current end date.
              </p>
            </div>
          </AdminActionModal>
        );

      case 'suspend':
        return (
          <AdminActionModal
            {...modalProps}
            title="Suspend Subscription"
            message={`Suspend subscription for ${subscription.gym_name}? This will prevent billing and limit gym functionality.`}
            confirmText="Suspend"
            type="warning"
            onConfirm={() => handleSubscriptionAction('suspend', subscription)}
          >
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> Suspending will stop billing but may limit gym access to platform features.
              </p>
            </div>
          </AdminActionModal>
        );

      default:
        return null;
    }
  };

  // Revenue chart data (mock - would come from analytics endpoint)
  const revenueData = [
    { label: 'Jan', value: 25400 },
    { label: 'Feb', value: 28200 },
    { label: 'Mar', value: 31100 },
    { label: 'Apr', value: 29800 },
    { label: 'May', value: 33500 },
    { label: 'Jun', value: 36200 }
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <ExclamationTriangleIcon className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-900 mb-2">Error Loading Subscriptions</h2>
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={fetchSubscriptions}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <CreditCardIcon className="w-8 h-8" />
                Subscription Management
              </h1>
              <p className="text-gray-600">
                Manage gym subscriptions, billing cycles, and revenue analytics
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleFilterChange('expiring_soon', !filters.expiring_soon)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filters.expiring_soon 
                    ? 'bg-red-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filters.expiring_soon ? 'Show All' : 'Show Expiring Soon'}
              </button>
              <button
                onClick={fetchSubscriptions}
                disabled={loading}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Subscription Metrics */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <AdminMetricCard
              title="Total Subscriptions"
              value={metrics.total}
              subtitle="All gym subscriptions"
              icon={<CreditCardIcon className="w-6 h-6" />}
              color="blue"
            />
            <AdminMetricCard
              title="Active Subscriptions"
              value={metrics.active}
              subtitle="Currently active"
              icon={<CheckCircleIcon className="w-6 h-6" />}
              color="green"
            />
            <AdminMetricCard
              title="Expiring Soon"
              value={metrics.expiring_soon}
              subtitle="Within 7 days"
              icon={<ExclamationTriangleIcon className="w-6 h-6" />}
              color="orange"
              trend={metrics.expiring_soon > 0 ? `${metrics.expiring_soon} need attention` : null}
              trendDirection={metrics.expiring_soon > 0 ? 'down' : 'neutral'}
            />
            <AdminMetricCard
              title="Expired"
              value={metrics.expired}
              subtitle="Require renewal"
              icon={<XCircleIcon className="w-6 h-6" />}
              color="red"
            />
          </div>
        </section>

        {/* Revenue Analytics */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Revenue Analytics</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AdminChart
              type="line"
              data={revenueData}
              title="Monthly Recurring Revenue"
              subtitle="Subscription revenue over time"
              height="300px"
              color="green"
              loading={loading}
            />
            <AdminChart
              type="pie"
              data={[
                { label: 'Active', value: metrics.active },
                { label: 'Expiring Soon', value: metrics.expiring_soon },
                { label: 'Expired', value: metrics.expired }
              ]}
              title="Subscription Status Distribution"
              subtitle="Current subscription breakdown"
              height="300px"
              color="blue"
              loading={loading}
            />
          </div>
        </section>

        {/* Subscription Table */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">All Subscriptions</h2>
          <AdminDataTable
            data={subscriptions}
            columns={columns}
            searchable={true}
            sortable={true}
            filterable={true}
            filters={statusFilters}
            loading={loading}
            emptyMessage="No subscriptions found"
            onRowClick={(subscription) => console.log('View subscription details:', subscription)}
          />
          
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border border-gray-200 rounded-lg">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} subscriptions
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleFilterChange('page', pagination.page - 1)}
                  disabled={!pagination.has_prev}
                  className="px-3 py-1 bg-gray-100 text-gray-600 rounded disabled:opacity-50 hover:bg-gray-200"
                >
                  Previous
                </button>
                <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded">
                  {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => handleFilterChange('page', pagination.page + 1)}
                  disabled={!pagination.has_next}
                  className="px-3 py-1 bg-gray-100 text-gray-600 rounded disabled:opacity-50 hover:bg-gray-200"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Platform Subscription Plans */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Platform Subscription Plans</h2>
            <button
              onClick={() => openPlanModal('createPlan')}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Add Plan
            </button>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
            {plansLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Plan Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Price</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Billing Cycle</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Recommended</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Active</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan) => (
                      <tr key={plan.id} className="border-b border-gray-100">
                        <td className="py-3 px-4 text-sm text-gray-900">{plan.plan_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-900">₹{plan.price}</td>
                        <td className="py-3 px-4 text-sm text-gray-900">{plan.billing_cycle}</td>
                        <td className="py-3 px-4">
                          {plan.recommended ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Yes</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">No</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {plan.is_active ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Yes</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">No</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openPlanModal('editPlan', plan);
                              }}
                              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                            >
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openPlanModal('deletePlan', plan);
                              }}
                              className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {plans.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No plans found. Add your first plan!
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Action Modals */}
      {renderModal()}
    </div>
  );
};

export default SubscriptionManagement;